import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authLoginUrl } from '../api/client';
import { fetchAuthMe, logout as logoutRequest } from '../api/auth';
import type { AuthUser } from '../api/types';

interface AuthContextValue {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  isAdmin: boolean;
  login: () => void;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function userIsAdmin(user: AuthUser | null) {
  return user?.role === 'ADMINISTRATOR' || user?.role === 'SYSOP';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchAuthMe,
    retry: false,
    staleTime: 30_000
  });

  const logoutMutation = useMutation({
    mutationFn: logoutRequest,
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });

  const user = meQuery.data?.user ?? null;
  const isAuthenticated = Boolean(user ?? meQuery.data?.authenticated);

  const value = useMemo<AuthContextValue>(() => ({
    isLoading: meQuery.isLoading,
    isAuthenticated,
    user,
    isAdmin: userIsAdmin(user),
    login: () => {
      window.location.assign(authLoginUrl);
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
    refetch: async () => {
      await meQuery.refetch();
    }
  }), [isAuthenticated, logoutMutation, meQuery, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
